/**
* MantencionesPage component
* @returns <MantencionesPage />
*/

// Import necessary components
import TitleAndButton from "../components/TitleAndButton";
import Breadcrumbs from "../components/Breadcrumbs";
import StoreGridContainer from "../components/StoreGridContainer";
import FilterContainer from "../components/FilterContainer";
import FiltersContainer from "../components/FiltersContainer";
import BoxWithBackgroundContainer from "../components/BoxWithBackgroundContainer";
import InputRadio from "../components/InputRadio";
import Accordion from "../components/Accordion";
import Button from "../components/Button";
import Drawer from "../components/Drawer";
import LegalFooter from "../components/LegalFooter";
import BannerDiscounts from "../components/BannerDiscounts";

// Import necessary hooks and utilities
import { useState } from "react";
import { useGetAllMaintenancePlansQuery, useGetDiscountsQuery, useGetLegalesQuery } from "../redux/apiSlice";
import { useSearchParams } from "react-router-dom";
import { uniqueModels, uniqueVersions, uniqueTypes } from "../scripts/helpers/uniqueReducers";
import Loader from "../components/Loader";
import { AnimatePresence } from "framer-motion";

// MantencionesPage component
const MantencionesPage = () => {
    // Get search params from URL
    const [searchParams, setSearchParams] = useSearchParams();

    const data = {
        image: "",
        title: "¿Para qué modelo estás buscando?",
        filters: {
            type: {
                title: "Tipo",
                options: [
                    {
                        type: "radio",
                        id: "type-all",
                        label: "Todos",
                        name: "type-all",
                        value: "all",
                        isRequired: false,
                    }
                ]
            },
            model: {
                title: "Serie",
                options: [
                    {
                        type: "radio",
                        id: "model-all",
                        label: "Todos",
                        name: "model-all",
                        value: "all",
                        isRequired: false,
                    }
                ]
            },
            version: {
                title: "Versión",
                options: [
                    {
                        type: "radio",
                        id: "version-all",
                        label: "Todas",
                        name: "version-all",
                        value: "all",
                        isRequired: false,
                    }
                ]
            }
        }
    }

    const {
        data: maintenancePlans,
        isLoading: maintenancePlansIsLoading,
        isFetching: maintenancePlansIsFetching,
        isSuccess: maintenancePlansIsSuccess,
        isError: maintenancePlansIsError
    } = useGetAllMaintenancePlansQuery()

    const {
        data: discounts,
        isLoading: discountsIsLoading,
        isFetching: discountsIsFetching,
        isSuccess: discountsIsSuccess,
        isError: discountsIsError
    } = useGetDiscountsQuery()

    // Fetch all legals using the useGetLegalesQuery hook
    const { 
        data: legals, 
        isLoading: legalsIsLoading, 
        isFetching: legalsIsFetching, 
        isSuccess: legalsIsSuccess, 
        isError: legalsIsError 
    } = useGetLegalesQuery();

    // State - initialize from URL params
    const [selectedType, setSelectedType] = useState(searchParams.get("segment") || "all");
    const [selectedModel, setSelectedModel] = useState(searchParams.get("serie") || "all");
    const [selectedVersion, setSelectedVersion] = useState(searchParams.get("version") || "all");
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    // Filters
    const filterByType = (type, items) => {
        // console.log(`filterByType ${type}`)
        if (type === "all") {
            return items;
        } else {
            const itemsFilteredByType = items.filter((item) => item.segment === type);
            return itemsFilteredByType;
        }
    }

    const filterByModel = (model, items) => {
        // console.log(`filterByType ${type}`)
        if (model === "all") {
            return items;
        } else {
            const itemsFilteredByModel = items.filter((item) => item.serie === model);
            return itemsFilteredByModel;
        }
    }

    const filterByVersion = (version, items) => {
        // console.log(`filterByType ${type}`)
        if (version === "all") {
            return items;
        } else {
            const itemsFilteredByModel = items.filter((item) => item.version.name === version);
            return itemsFilteredByModel;
        }
    }

    // Set the selected type to the value of the input. Ej: suv
    const typeChangeHandler = (e) => {
        const type = e.target.value;

        setSelectedType(type);

        // Reset selected model
        setSelectedModel(() => "all");

        // Reset selected version
        setSelectedVersion(() => "all");

        // Update URL
        const newParams = new URLSearchParams(searchParams);
        if (type && type !== "all") {
            newParams.set("segment", type);
        } else {
            newParams.delete("segment");
        }
        newParams.delete("serie");
        newParams.delete("version");
        setSearchParams(newParams);
    }

    // Set the selected model or serie to the value of the input. Ej: UX
    const modelChangeHandler = (e) => {
        const model = e.target.value;
        setSelectedModel(model);

        // Reset selected version
        setSelectedVersion(() => "all");

        // Update URL
        const newParams = new URLSearchParams(searchParams);
        if (model && model !== "all") {
            newParams.set("serie", model);
        } else {
            newParams.delete("serie");
        }
        newParams.delete("version");
        setSearchParams(newParams);
    }

    // Set the selected version to the value of the input. Ej: UX200
    const versionChangeHandler = (e) => {
        const version = e.target.value;

        setSelectedVersion(version);

        // Update URL
        const newParams = new URLSearchParams(searchParams);
        if (version && version !== "all") {
            newParams.set("version", version);
        } else {
            newParams.delete("version");
        }
        setSearchParams(newParams);
    };

    const toggleDrawer = () => {
        setIsDrawerOpen(!isDrawerOpen);
    };

    // Items filtered
    let filteredItems;

    // Type list
    let typeList;

    // Model List
    let modelList;

    // Version list
    let versionList;

    if (maintenancePlansIsSuccess) {
        filteredItems = filterByType(selectedType, maintenancePlans);
        filteredItems = filterByModel(selectedModel, filteredItems);
        filteredItems = filterByVersion(selectedVersion, filteredItems);

        typeList = uniqueTypes(maintenancePlans)

        modelList = uniqueModels(selectedType, maintenancePlans);
        versionList = uniqueVersions(selectedModel, maintenancePlans);
    }

    // Input lists and options lists for type, model, and version
    const typeInputList = data.filters.type.options.map((option, i) => {
        return (
            <InputRadio {...option} key={i} isChecked={selectedType === option.value} changed={typeChangeHandler} />
        )
    })

    const typeOptionsList = typeList?.map((item, i) => {
        return (
            <InputRadio
                type="radio"
                id={`type-${item}`}
                label={item.toUpperCase()}
                name={`type-${item}`}
                value={item}
                isRequired={false}
                key={i}
                isChecked={selectedType === item}
                changed={typeChangeHandler}
            />
        )
    })

    const modelInputList = data.filters.model.options.map((option, i) => {
        return (
            <InputRadio {...option} key={i} isChecked={selectedModel === option.value} changed={modelChangeHandler} />
        )
    })

    const modelOptionsList = modelList?.map((item, i) => {
        return (
            <InputRadio
                type="radio"
                id={`model-${item}`}
                label={item.toUpperCase()}
                name={`model-${item}`}
                value={item}
                isRequired={false}
                key={i}
                isChecked={selectedModel === item}
                changed={modelChangeHandler}
            />
        )
    })

    const versionInputList = data.filters.version.options.map((option, i) => {
        return (
            <InputRadio {...option} key={i} changed={versionChangeHandler} isChecked={selectedVersion === option.value} />
        )
    })

    const versionOptionList = versionList?.map((item, i) => {
        return (
            <InputRadio
                type="radio"
                id={`version-${item}`}
                label={item.toUpperCase()}
                name={`version-${item}`}
                value={item}
                isRequired={false}
                key={i}
                changed={versionChangeHandler}
                isChecked={selectedVersion === item}
            />
        )
    })

    // Drawer filters
    const drawerFilters = (
        [
            {
                label: "Tipo",
                content: (
                    <div>
                        {typeInputList}

                        {typeList && typeOptionsList}
                    </div>
                )
            },
            {
                label: "Modelo",
                content: (
                    <div>
                        {modelInputList}

                        {modelList && modelOptionsList}
                    </div>
                )
            },
            {
                label: "Versión",

                content: (
                    <div>
                        {versionInputList}

                        {versionList && versionOptionList}
                    </div>
                )
            }
        ]
    )

    // Drawer content
    const drawerContent = (
        <>
            <Accordion items={drawerFilters} type={"filters"} />
        </>
    );

    // BreadcrumbsProps and TitleAndButtonProps
    const BreadcrumbsProps = {
        links: [
            {
                link: "/",
                label: "Inicio"
            },
            {
                link: location.pathname,
                label: location.pathname.slice(1)
            },
        ],
    }

    const TitleAndButtonProps = {
        title: "Conoce todos <br> las mantenciones disponibles",
        buttons: [
            // {
            //     config: {
            //         tag: "button",
            //         text: "Encuentra tu Lexus",
            //         color: "black",
            //     },
            // }
        ],
        backgroundColor: "white",
        extraClass: ""
    }

    return (
        <>
            {maintenancePlansIsLoading && (
                <AnimatePresence>
                    <Loader />
                </AnimatePresence>
            )}

            {maintenancePlansIsSuccess && (
                <>
                    <Breadcrumbs {...BreadcrumbsProps} />

                    <TitleAndButton {...TitleAndButtonProps} />

                    {discountsIsSuccess && discounts?.maintenance && <BannerDiscounts content={discounts.maintenance}/>}

                    <StoreGridContainer>
                        <Button tag="link" text="Filtrar por" hasIcon={"true"} onClick={toggleDrawer} />

                        <FiltersContainer>
                            <h3>{data.title}</h3>

                            <form action="">
                                {/* Type, model and version */}
                                <fieldset className="fieldset-1">
                                    {/* Type */}
                                    <FilterContainer title={data.filters.type.title}>
                                        {typeInputList}

                                        {typeList && typeOptionsList}
                                    </FilterContainer>

                                    {/* Model */}
                                    <FilterContainer title={data.filters.model.title}>
                                        {modelInputList}

                                        {modelList && modelOptionsList}
                                    </FilterContainer>

                                    {/* Version */}
                                    <FilterContainer title={data.filters.version.title}>
                                        {versionInputList}

                                        {versionList && versionOptionList}
                                    </FilterContainer>
                                </fieldset>
                            </form>
                        </FiltersContainer>

                        <BoxWithBackgroundContainer items={filteredItems} extraClass="--store-version" />

                        <Drawer title={"¿Qué modelo estás buscando?"} content={drawerContent} isOpen={isDrawerOpen} close={toggleDrawer} />
                    </StoreGridContainer>
                </>
            )}

            {maintenancePlansIsError && <p>Hubo un error</p>}

            {legalsIsSuccess && <LegalFooter content={legals.maintenances}/>}
        </>
    )
}

export default MantencionesPage